import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { BiContainer, ConfirmationModal, Divider, Row, TextButton, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { getBillClientServiceOptions, getTimeActivitiesServiceOptions } from '~/business-logic/billServiceSelector';
import BillChargeTableItem from '~/components/BillClientModal/BillChargeTableItem/BillChargeTableItem';
import { billingStagesTextsPath, createEmptyChargeItem, createEmptyServiceItem, createServiceItemWithStages, getStageNote, getSubmitChargesData } from '~/components/BillClientModal/BillClientItem.utils';
import { billClientLoader, BillingType } from '~/components/BillClientModal/BillClientModal.types';
import BillingDate from '~/components/BillClientModal/BillingDate/BillingDate';
import { getCtaText, normalizeEffectiveDate } from '~/components/BillClientModal/BillingDate/BillingDate.utils';
import { useBillingModeData } from '~/components/BillClientModal/BillingDate/useBillingModeData';
import BillServiceTableItem from '~/components/BillClientModal/BillServiceTableItem/BillServiceTableItem';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useNonNullableRelationship } from '~/hooks/reactQuery/queries/useRelationship';
import { useAttachFiles, DEFAULT_MAX_TOTAL_SIZE } from '~/hooks/useAttachFiles';
import { useLocaleKeys } from '~/i18n';
import { ServerError } from '~/infra/ErrorResponse';
import { handleRequest } from '~/infra/utils';
import { relationshipModel, TimeActivitiesModel } from '~/models';
import { useBillingObservable } from '~/providers/BillingObservableProvider';
import { BIRelationshipScopeProvider } from '~/providers/BIRelationshipScopeProvider';
import { selectors } from '~/store/state-selectors';
import AttachFiles from '../AttachFiles/AttachFiles';
import { Attachments } from '../Attachments/Attachments';
import AddNewItemButtons from './AddNewItemButtons/AddNewItemButtons';
import styles from './BillClientModal.module.scss';
import { getBiCode } from './BillClientModal.utils';
import { BillClientTableAction } from './BillClientTableAction';
import { CreditsWarningCallout, RelationshipEffectiveDateCallout } from './Callouts/Callouts';
import { useSubmitCharges } from './hooks/useSubmitCharges';
import TimeActivitiesDrawer from './TimeActivitiesDrawer/TimeActivitiesDrawer';
export const GetBillClientModalTID = (parentTestId)=>getTestIds(parentTestId);
function ScopedBillClientModal(props) {
    const { manualServicesBilling, invoiceDateInfo, totalCredits, dismissModal, earliestBillableDate, defaultItem, controlData } = props;
    const { BillClientModalTID } = useMemo(()=>GetBillClientModalTID(), []);
    const { relationship, optimisticUpdate } = useNonNullableRelationship(props.relationshipId);
    const { notifyToRefresh } = useBillingObservable();
    const overlayContext = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: globalBillTexts } = useLocaleKeys('components.globalBillModal');
    const { t: billTexts } = useLocaleKeys('components.billClientModal');
    const { logBI } = useStoreActions();
    const { getNow } = useIoContext();
    const { t: stagesTexts } = useLocaleKeys(billingStagesTextsPath);
    const { t: attachmentErrors } = useLocaleKeys('invoice.attachments.uploadErrors');
    const allowTimeActivities = useStore(selectors.integration.allowTimeActivities);
    function getAttachmentErrorMessage(error) {
        if (error.hasServerError(ServerError.DuplicateFileNameNotAllowed)) {
            return attachmentErrors.duplicateFileName();
        }
        if (error.hasServerError(ServerError.AttachmentsTotalSizeExceeded)) {
            return attachmentErrors.totalSizeExceeded({
                maxSizeMB: DEFAULT_MAX_TOTAL_SIZE / (1024 * 1024)
            });
        }
        if (error.hasServerError(ServerError.AttachmentNotFound)) {
            return attachmentErrors.uploadFailed();
        }
        return undefined;
    }
    const [showTimeActivities, setShowTimeActivities] = useState(false);
    const timeActivities = useStore((state)=>{
        var _state_qboTimeActivities_relationship_id;
        return (_state_qboTimeActivities_relationship_id = state.qboTimeActivities[relationship.id]) === null || _state_qboTimeActivities_relationship_id === void 0 ? void 0 : _state_qboTimeActivities_relationship_id.data;
    }) || new TimeActivitiesModel();
    const { submitCharges } = useSubmitCharges();
    const vendorId = useStore(selectors.user.businessId);
    const userId = useStore(selectors.user.id);
    const { attachments, openFilePicker, isUploading, handleRemove } = useAttachFiles();
    const timeActivitiesServiceOptions = useMemo(()=>{
        if (!allowTimeActivities) return [];
        const options = getTimeActivitiesServiceOptions({
            services: relationship.services,
            controlData,
            defaultServiceId: defaultItem.type === BillingType.Service ? defaultItem.service.id : undefined
        });
        return options;
    }, [
        allowTimeActivities,
        controlData,
        defaultItem,
        relationship.services
    ]);
    const { billModeOptions, defaultMode, defaultIssueDate } = useBillingModeData({
        invoiceDateInfo,
        effectiveDate: relationship.effectiveDate,
        earliestBillableDate
    });
    const form = useForm({
        mode: 'onSubmit',
        defaultValues: {
            items: [
                defaultItem
            ],
            mode: defaultMode,
            issueDate: defaultIssueDate
        }
    });
    const itemsControl = useFieldArray({
        control: form.control,
        name: 'items'
    });
    const [mode, issueDate, items] = form.watch([
        'mode',
        'issueDate',
        'items'
    ]);
    const serviceOptions = useMemo(()=>{
        return getBillClientServiceOptions({
            items,
            services: relationship.services,
            controlData
        });
    }, [
        controlData,
        relationship.services,
        items
    ]);
    async function onSubmit(data) {
        const { mode, items } = data;
        const hasTimeActivity = items.some((item)=>item.type === BillingType.Service && item.timeActivityId);
        if (hasTimeActivity) logBI(12017, {
            vendorId,
            userId
        });
        overlayContext.startLoader(billClientLoader);
        const stuckTimer = setTimeout(()=>{
            var _navigator_connection;
            Sentry.captureException(new Error('BillClientModal: submit stuck loading for over 10s'), {
                extra: {
                    relationshipId: relationship.id,
                    vendorId,
                    userId,
                    mode,
                    itemCount: items.length,
                    isUploading,
                    formErrors: JSON.stringify(form.formState.errors),
                    navigatorOnLine: navigator.onLine,
                    connectionType: (_navigator_connection = navigator.connection) === null || _navigator_connection === void 0 ? void 0 : _navigator_connection.effectiveType
                }
            });
            overlayContext.stopLoader(billClientLoader);
        }, 10000);
        try {
            const { success, error } = await handleRequest(sendRequest(data));
            if (!success) {
                var _getAttachmentErrorMessage;
                const message = (_getAttachmentErrorMessage = getAttachmentErrorMessage(error)) !== null && _getAttachmentErrorMessage !== void 0 ? _getAttachmentErrorMessage : 'Failed to bill client';
                overlayContext.showErrorToast(message, "bill-client-".concat(mode, "-error-toast"));
            } else {
                await notifyToRefresh();
                dismissModal();
                overlayContext.showSuccessToast(commonTexts.toasts.chargesSuccessfullyCreated(), "bill-client-".concat(mode, "-success-toast"));
            }
        } finally{
            clearTimeout(stuckTimer);
            overlayContext.stopLoader(billClientLoader);
        }
    }
    async function sendRequest(data) {
        const { mode, issueDate } = data;
        const biCode = getBiCode(mode, issueDate, getNow());
        logBI(biCode);
        const submitChargesData = getSubmitChargesData(data, allowTimeActivities && timeActivities.hasServices() ? timeActivities : undefined, attachments.map((att)=>({
                id: att.id,
                displayName: att.displayName
            })));
        return submitCharges({
            relationshipId: relationship.id,
            data: submitChargesData
        });
    }
    const handleCloseModal = ()=>{
        logBI(18404);
        dismissModal();
    };
    const handleDeleteItem = (itemIndex)=>{
        logBI(18405);
        itemsControl.remove(itemIndex);
    };
    const onAddOutOfScopeCharge = ()=>{
        logBI(18407);
        itemsControl.append(createEmptyChargeItem());
    };
    const onAddServiceFromAgreement = (param)=>{
        let { serviceId } = param;
        logBI(18406, {
            relationshipId: relationship.id
        });
        const service = relationship.services.find((s)=>s.id === serviceId);
        if (service) {
            itemsControl.append(createEmptyServiceItem(service));
        } else {
            throw new Error("Service with id ".concat(serviceId, " not found for bill client modal add service button"));
        }
    };
    const onAddServiceInStages = (param)=>{
        let { serviceId, stageNumber, billingStages } = param;
        logBI(18406, {
            relationshipId: relationship.id
        });
        const service = relationship.services.find((s)=>s.id === serviceId);
        if (!service) throw new Error("Service with id ".concat(serviceId, " not found for bill client modal add service button"));
        itemsControl.append(createServiceItemWithStages({
            service,
            stageNumber,
            billingStages,
            note: getStageNote({
                stagesLength: billingStages.length,
                stageNumber,
                t: stagesTexts
            })
        }));
    };
    const renderTableItem = (item, itemIndex)=>{
        switch(item.type){
            case BillingType.Service:
                return /*#__PURE__*/ _jsx(BillServiceTableItem, {
                    serviceItem: item,
                    itemIndex: itemIndex,
                    relationshipEffectiveDate: normalizeEffectiveDate(relationship.effectiveDate),
                    manualServiceBilling: manualServicesBilling[item.service.id],
                    actionColumn: /*#__PURE__*/ _jsx(BillClientTableAction, {
                        showDeleteButton: items.length > 1,
                        item: item,
                        itemIndex: itemIndex,
                        timeActivities: timeActivities,
                        allowTimeActivities: allowTimeActivities,
                        onDeleteItem: handleDeleteItem
                    })
                }, item.itemId);
            case BillingType.Charge:
                return /*#__PURE__*/ _jsx(BillChargeTableItem, {
                    itemIndex: itemIndex,
                    chargeItem: item,
                    relationship: relationship,
                    invoiceDateInfo: invoiceDateInfo,
                    actionColumn: /*#__PURE__*/ _jsx(BillClientTableAction, {
                        showDeleteButton: items.length > 1,
                        item: item,
                        itemIndex: itemIndex,
                        timeActivities: timeActivities,
                        allowTimeActivities: allowTimeActivities,
                        onDeleteItem: handleDeleteItem
                    })
                }, item.itemId);
        }
    };
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        title: globalBillTexts.billClientTitle({
            customerName: relationship.customer.name
        }),
        width: "1600px",
        primaryButtonLabel: getCtaText({
            mode,
            ctaTexts: billTexts.ctaLabels
        }),
        primaryButtonDisabled: isUploading,
        testId: BillClientModalTID.Root(),
        textAlignCenter: false,
        onPrimaryButtonClick: form.handleSubmit(onSubmit),
        onCloseButtonClick: handleCloseModal,
        bi: {
            primaryButton: {
                name: 'bill'
            },
            secondaryButton: {
                name: 'cancel'
            },
            closeButton: {
                name: 'close'
            }
        },
        body: !!timeActivitiesServiceOptions.length && /*#__PURE__*/ _jsx(TimeActivitiesDrawer, {
            open: showTimeActivities,
            relationship: relationship,
            itemsControl: itemsControl,
            serviceOptions: timeActivitiesServiceOptions,
            timeActivities: timeActivities,
            close: ()=>setShowTimeActivities(false),
            getValues: form.getValues,
            setRelationship: optimisticUpdate
        }),
        content: /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, form), {
            children: /*#__PURE__*/ _jsx(BiContainer, {
                name: "bill_client",
                data: {
                    relationshipId: relationship.id,
                    clientId: relationship.customer.id
                },
                children: /*#__PURE__*/ _jsx("div", {
                    className: styles.ensureNoHorizontalScrollFromDrawer,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: styles.modalContentWrapper,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles.billModalDatesWrapper,
                                children: [
                                    /*#__PURE__*/ _jsx(BillingDate, {
                                        earliestBillableDate: earliestBillableDate,
                                        billModeOptions: billModeOptions,
                                        invoiceDateInfo: invoiceDateInfo,
                                        effectiveDate: relationship.effectiveDate
                                    }),
                                    /*#__PURE__*/ _jsxs(Row, {
                                        gap: 16,
                                        alignItems: "center",
                                        children: [
                                            /*#__PURE__*/ _jsx(AttachFiles, {
                                                attachments: attachments,
                                                openFilePicker: openFilePicker,
                                                isUploading: isUploading
                                            }),
                                            allowTimeActivities && /*#__PURE__*/ _jsx(Divider, {
                                                orientation: "vertical",
                                                margin: 0,
                                                size: 20
                                            }),
                                            allowTimeActivities && /*#__PURE__*/ _jsx(TextButton, {
                                                tooltip: !timeActivitiesServiceOptions.length ? {
                                                    title: billTexts.noActiveUnitBasedServices(),
                                                    placement: 'top'
                                                } : undefined,
                                                disabled: !timeActivitiesServiceOptions.length,
                                                weight: "normal",
                                                className: styles.showTimeActivitiesText,
                                                testId: BillClientModalTID.ShowTimeActivities(),
                                                bi: {
                                                    name: 'show_time_activities'
                                                },
                                                onClick: ()=>setShowTimeActivities(true),
                                                underline: "none",
                                                children: billTexts.showTimeActivities()
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(CreditsWarningCallout, {
                                totalCredits: totalCredits,
                                testId: BillClientModalTID.HasCreditsCallout()
                            }),
                            /*#__PURE__*/ _jsx(RelationshipEffectiveDateCallout, {
                                issueDate: issueDate,
                                effectiveDate: relationship.effectiveDate,
                                testId: BillClientModalTID.IssueDateBeforeRelationshipEffectiveDateCallout()
                            }),
                            items.map(renderTableItem),
                            attachments.length > 0 && /*#__PURE__*/ _jsx(Attachments, {
                                files: attachments,
                                onRemove: handleRemove,
                                disableDownloadLinks: true
                            }),
                            /*#__PURE__*/ _jsx(AddNewItemButtons, {
                                serviceOptions: serviceOptions,
                                handleAddOutOfScopeCharge: onAddOutOfScopeCharge,
                                handleAddServiceFromAgreement: onAddServiceFromAgreement,
                                handleAddServiceInStages: onAddServiceInStages
                            })
                        ]
                    })
                })
            })
        }))
    });
}
export default function BillClientModal(props) {
    const { relationship } = useNonNullableRelationship(props.relationshipId);
    const isApproved = relationshipModel.isApproved(relationship);
    return /*#__PURE__*/ _jsx(BIRelationshipScopeProvider, {
        relationshipScope: {
            id: props.relationshipId,
            isApproved
        },
        children: /*#__PURE__*/ _jsx(ScopedBillClientModal, _object_spread({}, props))
    });
}
